import React, { useState, useEffect, useCallback } from 'react';
import { formatPercentage, formatCurrency } from '../../../utils/formatters';
import {
  configService,
  StoreTax,
  StoreTaxCreate,
  StoreTaxUpdate,
} from '../../../services/configService';
import { authService } from '../../../services/auth/authService';
import {
  EffectiveFeeCategory,
  StoreTaxAppliedFee,
  feeCategoryService,
  formatCategoryName,
} from '../../../services/feeCategoryService';

interface TaxFormData {
  tax_name: string;
  tax_code: string;
  rate_type: 'percentage' | 'flat';
  rate_value: number;
  unit_type: 'once' | 'night' | 'person' | 'site';
  tax_authority: string;
  description: string;
  priority: number;
  active: boolean;
  applied_category_ids: number[]; // Fee categories this tax applies to
}

const TaxCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [taxes, setTaxes] = useState<StoreTax[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTax, setEditingTax] = useState<StoreTax | null>(null);
  const [formData, setFormData] = useState<TaxFormData>({
    tax_name: '',
    tax_code: '',
    rate_type: 'percentage',
    rate_value: 0,
    unit_type: 'once',
    tax_authority: '',
    description: '',
    priority: 0,
    active: true,
    applied_category_ids: [],
  });
  const [saving, setSaving] = useState(false);
  // Fee categories state
  const [feeCategories, setFeeCategories] = useState<EffectiveFeeCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [taxAppliedFees, setTaxAppliedFees] = useState<Map<number, StoreTaxAppliedFee[]>>(new Map());

  const storeId = authService.getStoreId();

  const loadTaxes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('TaxCard: Loading taxes for storeId:', storeId);
      const taxList = await configService.getStoreTaxes(storeId, false);
      console.log('TaxCard: Received taxes:', taxList);
      setTaxes(taxList);

      // Load tax applied fees for all taxes
      if (storeId && taxList.length > 0) {
        const allAppliedFees = await feeCategoryService.getStoreTaxAppliedFees(storeId);
        // Group by store_tax_id
        const grouped = new Map<number, StoreTaxAppliedFee[]>();
        allAppliedFees.forEach(fee => {
          const existing = grouped.get(fee.store_tax_id) || [];
          existing.push(fee);
          grouped.set(fee.store_tax_id, existing);
        });
        setTaxAppliedFees(grouped);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load taxes');
      console.error('Failed to load taxes:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const loadFeeCategories = useCallback(async () => {
    if (!storeId) {
      console.warn('TaxCard: No storeId found, cannot load categories');
      setCategoriesLoading(false);
      return;
    }
    try {
      setCategoriesLoading(true);
      console.log('TaxCard: Loading fee categories for storeId:', storeId);
      // Get all active fee categories (taxes can apply to any category type)
      const categories = await feeCategoryService.getEffectiveFeeCategories(storeId, {
        activeOnly: true,
      });
      console.log('TaxCard: Received categories:', categories);
      // Filter to taxable categories only
      const taxableCategories = categories.filter(c => c.is_taxable);
      console.log('TaxCard: Taxable categories:', taxableCategories);
      setFeeCategories(taxableCategories);
    } catch (err) {
      console.error('Failed to load fee categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  }, [storeId]);

  // Fetch taxes and categories on mount
  useEffect(() => {
    loadTaxes();
    loadFeeCategories();
  }, [loadTaxes, loadFeeCategories]);

  const handleDelete = async (taxId: number, taxName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${taxName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await configService.deleteStoreTax(taxId);
      setTaxes(taxes.filter(tax => tax.id !== taxId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete tax');
    }
  };

  const openAddModal = () => {
    setEditingTax(null);
    setFormData({
      tax_name: '',
      tax_code: '',
      rate_type: 'percentage',
      rate_value: 0,
      unit_type: 'once',
      tax_authority: '',
      description: '',
      priority: 0,
      active: true,
      applied_category_ids: [],
    });
    setShowModal(true);
  };

  const openEditModal = (tax: StoreTax) => {
    setEditingTax(tax);
    // Get applied category IDs for this tax
    const appliedFees = taxAppliedFees.get(tax.id) || [];
    const appliedCategoryIds = appliedFees.map(f => f.fee_category_id);
    setFormData({
      tax_name: tax.tax_name,
      tax_code: tax.tax_code || '',
      rate_type: tax.rate_type,
      rate_value: tax.rate_value,
      unit_type: tax.unit_type,
      tax_authority: tax.tax_authority || '',
      description: tax.description || '',
      priority: tax.priority,
      active: tax.active,
      applied_category_ids: appliedCategoryIds,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.tax_name.trim()) {
      alert('Please enter a tax name');
      return;
    }
    if (formData.rate_value <= 0) {
      alert('Please enter a valid rate value');
      return;
    }
    if (formData.applied_category_ids.length === 0) {
      alert('Please select at least one fee category this tax applies to');
      return;
    }

    setSaving(true);
    try {
      let taxId: number;

      if (editingTax) {
        // Update existing tax
        const updateData: StoreTaxUpdate = {
          tax_name: formData.tax_name,
          tax_code: formData.tax_code || undefined,
          rate_type: formData.rate_type,
          rate_value: formData.rate_value,
          unit_type: formData.unit_type,
          tax_authority: formData.tax_authority || undefined,
          description: formData.description || undefined,
          priority: formData.priority,
          active: formData.active,
        };
        const updated = await configService.updateStoreTax(editingTax.id, updateData);
        setTaxes(taxes.map(t => t.id === editingTax.id ? updated : t));
        taxId = editingTax.id;
      } else {
        // Create new tax
        if (!storeId) {
          alert('Store ID not found. Please log in again.');
          return;
        }
        const createData: StoreTaxCreate = {
          store_id: storeId,
          tax_name: formData.tax_name,
          tax_code: formData.tax_code || undefined,
          rate_type: formData.rate_type,
          rate_value: formData.rate_value,
          unit_type: formData.unit_type,
          tax_authority: formData.tax_authority || undefined,
          description: formData.description || undefined,
          priority: formData.priority,
          active: formData.active,
        };
        const created = await configService.createStoreTax(createData);
        setTaxes([...taxes, created]);
        taxId = created.id;
      }

      // Sync tax-to-category mappings
      if (storeId) {
        await feeCategoryService.syncTaxAppliedFeeCategories(
          storeId,
          taxId,
          formData.applied_category_ids
        );
        // Reload tax applied fees to update the display
        const allAppliedFees = await feeCategoryService.getStoreTaxAppliedFees(storeId);
        const grouped = new Map<number, StoreTaxAppliedFee[]>();
        allAppliedFees.forEach(fee => {
          const existing = grouped.get(fee.store_tax_id) || [];
          existing.push(fee);
          grouped.set(fee.store_tax_id, existing);
        });
        setTaxAppliedFees(grouped);
      }

      setShowModal(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save tax');
    } finally {
      setSaving(false);
    }
  };

  const formatRateDisplay = (tax: StoreTax) => {
    if (tax.rate_type === 'percentage') {
      return formatPercentage(tax.rate_value);
    }
    return formatCurrency(tax.rate_value);
  };

  const getUnitTypeLabel = (unitType: string) => {
    const labels: Record<string, string> = {
      once: 'Per Booking',
      night: 'Per Night',
      person: 'Per Person',
      site: 'Per Site',
    };
    return labels[unitType] || unitType;
  };

  const getAppliedCategoriesDisplay = (taxId: number) => {
    const appliedFees = taxAppliedFees.get(taxId) || [];
    if (appliedFees.length === 0) {
      return <span className="text-muted">None</span>;
    }
    const categoryNames = appliedFees
      .map(fee => {
        const category = feeCategories.find(c => c.id === fee.fee_category_id);
        return category ? formatCategoryName(category.category_name) : `Category ${fee.fee_category_id}`;
      })
      .join(', ');
    return categoryNames;
  };

  const toggleCategorySelection = (categoryId: number) => {
    const currentIds = formData.applied_category_ids;
    if (currentIds.includes(categoryId)) {
      setFormData({
        ...formData,
        applied_category_ids: currentIds.filter(id => id !== categoryId),
      });
    } else {
      setFormData({
        ...formData,
        applied_category_ids: [...currentIds, categoryId],
      });
    }
  };

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">%</span>
          <h2>Tax</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/store_taxes</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            {String.fromCharCode(9660)}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {loading ? (
            <div className="loading-state">Loading taxes...</div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-primary btn-sm" onClick={loadTaxes}>
                Retry
              </button>
            </div>
          ) : taxes.length === 0 ? (
            <div className="empty-state">
              <p>No taxes configured yet.</p>
            </div>
          ) : (
            <table className="config-table">
              <thead>
                <tr>
                  <th>Tax Name</th>
                  <th>Rate</th>
                  <th>Unit</th>
                  <th>Applies To Fee Categories</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {taxes.map(tax => (
                  <tr key={tax.id}>
                    <td>{tax.tax_name}</td>
                    <td className="amount">{formatRateDisplay(tax)}</td>
                    <td>{getUnitTypeLabel(tax.unit_type)}</td>
                    <td className="categories-cell">{getAppliedCategoriesDisplay(tax.id)}</td>
                    <td className={tax.active ? 'status-active' : 'status-inactive'}>
                      {tax.active ? 'Active' : 'Inactive'}
                    </td>
                    <td>
                      <div className="config-actions">
                        <button
                          className="btn btn-text btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(tax);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-text btn-sm btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(tax.id, tax.tax_name);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="config-card-footer">
            <button className="btn btn-primary btn-sm" onClick={openAddModal}>
              + Add Tax
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTax ? 'Edit Tax' : 'Add Tax'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                {String.fromCharCode(215)}
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Tax Name *</label>
                <input
                  type="text"
                  value={formData.tax_name}
                  onChange={(e) => setFormData({ ...formData, tax_name: e.target.value })}
                  placeholder="e.g., State Sales Tax, Lodging Tax"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Tax Code</label>
                  <input
                    type="text"
                    value={formData.tax_code}
                    onChange={(e) => setFormData({ ...formData, tax_code: e.target.value })}
                    placeholder="e.g., ME-LODG-9"
                  />
                </div>
                <div className="form-group">
                  <label>Tax Authority</label>
                  <input
                    type="text"
                    value={formData.tax_authority}
                    onChange={(e) => setFormData({ ...formData, tax_authority: e.target.value })}
                    placeholder="e.g., State of Maine"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Rate Type *</label>
                  <select
                    value={formData.rate_type}
                    onChange={(e) => setFormData({ ...formData, rate_type: e.target.value as 'percentage' | 'flat' })}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="flat">Flat Amount ($)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Rate Value *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.rate_value || ''}
                    onChange={(e) => setFormData({ ...formData, rate_value: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    placeholder={formData.rate_type === 'percentage' ? '9.0' : '5.00'}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Applies To *</label>
                  <select
                    value={formData.unit_type}
                    onChange={(e) => setFormData({ ...formData, unit_type: e.target.value as 'once' | 'night' | 'person' | 'site' })}
                  >
                    <option value="once">Per Booking (Once)</option>
                    <option value="night">Per Night</option>
                    <option value="person">Per Person</option>
                    <option value="site">Per Site</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <small className="form-hint">Lower numbers apply first</small>
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description for this tax"
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label>Applies To Fee Categories *</label>
                {categoriesLoading ? (
                  <div className="form-hint">Loading categories...</div>
                ) : feeCategories.length === 0 ? (
                  <div className="form-hint">No taxable fee categories found. Please configure fee categories first.</div>
                ) : (
                  <div className="checkbox-list">
                    {feeCategories.map(category => (
                      <label key={category.id} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.applied_category_ids.includes(category.id)}
                          onChange={() => toggleCategorySelection(category.id)}
                        />
                        {formatCategoryName(category.category_name)}
                      </label>
                    ))}
                  </div>
                )}
                <small className="form-hint">Select which fee types this tax applies to</small>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxCard;
